import { useEffect, useMemo, useRef, useState } from "react";
import { trainerGrade } from "@cimara/trainer";
import type { RaceStateMessage, Spawn } from "@cimara/protocol";
import type { World } from "@cimara/world";
import type { RaceRegistration } from "../api/types";
import { Hud } from "./Hud";
import { PenOverlay } from "./PenOverlay";
import { RideScene } from "../scene/RideScene";
import { CimaraSocket } from "../net/socket";
import { RideStore, type RideSummary } from "../ride/store";
import { useTrainer } from "../ride/useTrainer";

interface RideScreenProps {
  world: World | null;
  loading: boolean;
  error: string | null;
  getToken: () => Promise<string>;
  raceRegistration?: RaceRegistration | null;
  raceName?: string;
  onRetry: () => void;
  onFinish(summary: RideSummary): void;
  onRaceFinished(raceId: string): void;
}

const DEFAULT_SIM_POWER = 180;
const DEFAULT_DIFFICULTY = 0.5;

function LoadingHud({
  loading,
  error,
  onRetry,
}: {
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="ride-screen ride-screen--loading">
      <div className="loading-orbit" aria-hidden="true" />
      <div className="hud hud--loading" data-testid="hud">
        <header className="hud-top">
          <div className="hud-top__left">
            <div className="hud-brand">
              <span className="hud-brand__mark" />
              CIMARA
            </div>
            <div className="hud-route">Preparing the circuit</div>
          </div>
          <div className="hud-top__right">
            <div
              className="connection-pill connection-pill--connecting"
              data-testid="connection"
            >
              <span className="connection-pill__dot" />
              <span>{loading ? "Loading world" : "World unavailable"}</span>
            </div>
            <div className="hud-readouts">
              <span className="hud-readout">
                FPS <strong data-testid="fps">—</strong>
              </span>
              <span className="hud-readout">
                Riders <strong data-testid="rider-count">0</strong>
              </span>
            </div>
          </div>
        </header>

        <div className="hud-bottom">
          <section className="metrics" aria-label="Ride metrics">
            <div className="metric metric--power">
              <span className="metric__label">Power</span>
              <span className="metric__value" data-testid="power-value">
                0
              </span>
              <span className="metric__unit">W</span>
            </div>
            <div className="metric">
              <span className="metric__label">Speed</span>
              <span className="metric__value" data-testid="speed-value">
                0.0
              </span>
              <span className="metric__unit">km/h</span>
            </div>
            <div className="metric metric--grade">
              <span className="metric__label">Grade</span>
              <span className="metric__value metric__value--small" data-testid="grade-actual">
                0.0%
              </span>
              <span className="metric__value metric__value--small" data-testid="grade-effective">
                0.0%
              </span>
            </div>
          </section>
          <aside className="trainer-panel">
            <button
              type="button"
              className="trainer-toggle"
              data-testid="toggle-trainer-panel"
              disabled
            >
              <span className="trainer-toggle__title">
                <span className="trainer-dot trainer-dot--unavailable" />
                Trainer
              </span>
              <span className="trainer-toggle__action">Loading</span>
            </button>
            <div className="trainer-panel__head">
              <div>
                <span className="panel-kicker">Effort</span>
                <h2>Trainer</h2>
              </div>
            </div>
            <button className="trainer-connect" type="button" data-testid="connect-trainer" disabled>
              Connect trainer
            </button>
            <label className="control">
              <span className="control__row">
                <span>Trainer difficulty</span>
                <output className="control__value">50%</output>
              </span>
              <input
                data-testid="difficulty"
                className="slider"
                type="range"
                min={0}
                max={100}
                value={50}
                disabled
              />
            </label>
            <label className="control">
              <span className="control__row">
                <span>Sim power</span>
                <output className="control__value">{DEFAULT_SIM_POWER} W</output>
              </span>
              <input
                data-testid="sim-power"
                className="slider"
                type="range"
                min={0}
                max={500}
                value={DEFAULT_SIM_POWER}
                disabled
              />
            </label>
          </aside>
        </div>

        <div className="ride-loading-copy">
          <p className="panel-kicker">Phase 0 circuit</p>
          <h1>{loading ? "Building the road…" : "The world did not load"}</h1>
          <p>
            {loading
              ? "Fetching the manifest, verifying the SHA-256, and meshing the 15 km ribbon."
              : error ?? "The server did not return a usable world."}
          </p>
          {!loading ? (
            <button type="button" className="text-button" onClick={onRetry}>
              Try again
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function RideExperience({
  world,
  difficulty,
  simPower,
  getToken,
  raceRegistration,
  raceName,
  setDifficulty,
  setSimPower,
  onFinish,
  onRaceFinished,
}: {
  world: World;
  difficulty: number;
  simPower: number;
  getToken: () => Promise<string>;
  raceRegistration: RaceRegistration | null;
  raceName?: string;
  setDifficulty(value: number): void;
  setSimPower(value: number): void;
  onFinish(summary: RideSummary): void;
  onRaceFinished(raceId: string): void;
}) {
  const store = useMemo(() => new RideStore(world), [world]);
  const difficultyRef = useRef(difficulty);
  const initialRouteId = world.routes[0]?.id;
  const [routeId, setRouteId] = useState<number | undefined>(initialRouteId);
  const [startSpawn, setStartSpawn] = useState<Spawn>(world.spawn);
  const [raceState, setRaceState] = useState<RaceStateMessage | null>(null);
  const finishedRaceRef = useRef(false);
  const finishTimerRef = useRef<number | null>(null);
  const onRaceFinishedRef = useRef(onRaceFinished);
  onRaceFinishedRef.current = onRaceFinished;

  const trainer = useTrainer({
    onPower: (sample) => store.setTrainerPower(sample),
    onDisconnect: () => store.setTrainerPower(null),
  });

  const socket = useMemo(
    () =>
      new CimaraSocket({
        tokenProvider: getToken,
        callbacks: {
          onStatus: (state, detail) => store.setConnection(state, detail),
          onWelcome: (message) => store.applyWelcome(message),
          onSnapshot: (snapshot, receivedAt) => store.applySnapshot(snapshot, receivedAt),
          onRaceState: (message) => {
            store.applyRaceState(message);
            setRaceState(message);
            setRouteId((current) =>
              current === message.routeId ? current : message.routeId,
            );
            if (message.spawn) {
              const spawn = message.spawn;
              setStartSpawn((current) =>
                current.edge === spawn.edge &&
                current.s === spawn.s &&
                current.lane === spawn.lane
                  ? current
                  : spawn,
              );
            }
            if (
              !finishedRaceRef.current &&
              (message.phase === "finished" ||
                message.selfStatus === "finished" ||
                message.selfStatus === "dnf")
            ) {
              finishedRaceRef.current = true;
              finishTimerRef.current = window.setTimeout(
                () => onRaceFinishedRef.current(message.raceId),
                600,
              );
            }
          },
          onError: (message) => store.setError(message),
        },
      }),
    [store, getToken],
  );

  useEffect(() => {
    store.setSimPower(simPower);
  }, [store, simPower]);

  useEffect(() => {
    difficultyRef.current = difficulty;
  }, [difficulty]);

  useEffect(() => {
    if (!trainer.connected) store.setTrainerPower(null);
  }, [store, trainer.connected]);

  useEffect(() => {
    socket.connect();
    return () => socket.close();
  }, [socket]);

  useEffect(
    () => () => {
      if (finishTimerRef.current !== null) {
        window.clearTimeout(finishTimerRef.current);
        finishTimerRef.current = null;
      }
    },
    [],
  );

  // One power sample per second. The server integrates at 20 Hz.
  useEffect(() => {
    const send = (): void => {
      const now = Date.now();
      const power = store.getPower(now);
      socket.sendInput(power, store.getCadence(now));
      store.recordPower(power);
    };
    send();
    const timer = window.setInterval(send, 1000);
    return () => window.clearInterval(timer);
  }, [socket, store]);

  // Resistance is a comfort layer: send the difficulty-scaled grade only to
  // the trainer. The simulation never sees it.
  useEffect(() => {
    const apply = (): void => {
      trainer.applyGrade(trainerGrade(store.getActualGrade(), difficultyRef.current));
    };
    apply();
    const timer = window.setInterval(apply, 500);
    return () => window.clearInterval(timer);
  }, [store, trainer.applyGrade]);

  useEffect(() => {
    trainer.applyGrade(trainerGrade(store.getActualGrade(), difficulty));
  }, [difficulty, store, trainer.applyGrade]);

  const handleEnd = (): void => {
    const summary = store.getSummary(Date.now());
    socket.close();
    onFinish(summary);
  };

  return (
    <div className="ride-screen">
      <div className="scene-layer">
        <RideScene
          store={store}
          world={world}
          routeId={routeId}
          startSpawn={startSpawn}
        />
      </div>
      <div className="vignette" aria-hidden="true" />
      <Hud
        store={store}
        world={world}
        simPower={simPower}
        difficulty={difficulty}
        trainer={trainer}
        raceRegistration={raceRegistration}
        onSimPowerChange={setSimPower}
        onDifficultyChange={setDifficulty}
        onEndRide={handleEnd}
      />
      {raceState && (raceState.phase === "pen" || raceState.phase === "countdown") ? (
        <PenOverlay
          race={raceState}
          registration={raceRegistration}
          raceName={raceName}
        />
      ) : null}
    </div>
  );
}

export function RideScreen({
  world,
  loading,
  error,
  getToken,
  raceRegistration = null,
  raceName,
  onRetry,
  onFinish,
  onRaceFinished,
}: RideScreenProps) {
  const [simPower, setSimPower] = useState(DEFAULT_SIM_POWER);
  const [difficulty, setDifficulty] = useState(DEFAULT_DIFFICULTY);

  if (!world) {
    return <LoadingHud loading={loading} error={error} onRetry={onRetry} />;
  }

  return (
    <RideExperience
      world={world}
      simPower={simPower}
      difficulty={difficulty}
      getToken={getToken}
      raceRegistration={raceRegistration}
      raceName={raceName}
      setSimPower={setSimPower}
      setDifficulty={setDifficulty}
      onFinish={onFinish}
      onRaceFinished={onRaceFinished}
    />
  );
}
